/**
 * Staff Details - Team Composition Analytics
 * Charts and distribution visualizations
 */
import { 
  PieChart, Building2, Users, Target, BarChart3, 
  MessageSquare, AlertCircle, Lightbulb, Zap 
} from 'lucide-react';
import { COLOUR_BRAIN_TYPES } from './constants';
import TeamHealthScoreCard, { 
  calculateTeamHealthScore, 
  getImprovementTips 
} from './TeamHealthScore';

// Mini Pie Chart Component
const MiniPieChart = ({ data, colors, size = 120 }) => {
  const total = Object.values(data).reduce((a, b) => a + b, 0);
  if (total === 0) return <div className="text-gray-400 text-sm">Tiada data</div>;
  
  let cumulativePercent = 0;
  const segments = Object.entries(data).map(([key, value], index) => {
    const percent = (value / total) * 100;
    const startPercent = cumulativePercent;
    cumulativePercent += percent;
    return { key, value, percent, startPercent, color: colors[index % colors.length] };
  }).filter(s => s.value > 0);
  
  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg viewBox="0 0 36 36" className="w-full h-full transform -rotate-90">
        {segments.map((seg, i) => (
          <circle
            key={seg.key}
            cx="18" cy="18" r="15.9155"
            fill="transparent"
            stroke={seg.color}
            strokeWidth="3"
            strokeDasharray={`${seg.percent} ${100 - seg.percent}`}
            strokeDashoffset={-seg.startPercent}
            className="transition-all duration-500"
          />
        ))}
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">
        <span className="text-lg font-bold text-gray-700">{total}</span>
      </div>
    </div>
  );
};

// Progress Bar Component
const ProgressBar = ({ label, value, total, color, icon }) => {
  const percent = total > 0 ? (value / total) * 100 : 0;
  return (
    <div className="flex items-center gap-3">
      <span className="text-xl w-8">{icon}</span>
      <div className="flex-1">
        <div className="flex justify-between text-sm mb-1">
          <span className="font-medium">{label}</span>
          <span className="text-gray-500">{value} ({percent.toFixed(0)}%)</span>
        </div>
        <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
          <div 
            className={`h-full rounded-full transition-all duration-500 ${color}`}
            style={{ width: `${percent}%` }}
          />
        </div>
      </div>
    </div>
  );
};

const TeamCompositionAnalytics = ({ summary, detailsList, staffList }) => {
  if (!summary) return null;
  
  const vakdData = summary.vakd_distribution || {};
  const dopeData = summary.dope_distribution || {};
  const colourBrainData = summary.colour_brain_distribution || {};
  const leadershipData = summary.leadership_distribution || {};
  const teamRoles = summary.team_roles || {};
  const workStyles = summary.work_styles || {};
  const deptData = summary.by_department || {};
  
  const totalVakd = Object.values(vakdData).reduce((a, b) => a + b, 0);
  const totalDope = Object.values(dopeData).reduce((a, b) => a + b, 0);
  const totalColourBrain = Object.values(colourBrainData).reduce((a, b) => a + b, 0);
  
  const healthScore = calculateTeamHealthScore(summary);
  const improvementTips = getImprovementTips(healthScore.breakdown);
  
  // Get communication tips
  const getCommunicationTips = () => {
    const tips = [];
    const dominantVakd = Object.entries(vakdData).sort((a, b) => b[1] - a[1])[0];
    const dominantDope = Object.entries(dopeData).sort((a, b) => b[1] - a[1])[0];
    
    if (dominantVakd && dominantVakd[1] > 0) {
      const vakdTips = {
        'Visual': '📊 Gunakan lebih banyak visual aids dalam meeting',
        'Audio': '🎤 Brief secara lisan dan gunakan voice notes',
        'Kinestetik': '🛠️ Bagi peluang hands-on, kurangkan teori',
        'Digital': '📋 Sediakan SOP dengan step-by-step logic'
      };
      tips.push(vakdTips[dominantVakd[0]] || '');
    }
    
    if (dominantDope && dominantDope[1] > 0) {
      const dopeTips = {
        'Dove': '💚 Team perlukan appreciation dan stable environment',
        'Owl': '📊 Team perlukan data lengkap sebelum keputusan',
        'Peacock': '🌟 Team perlukan recognition dan excitement',
        'Eagle': '🎯 Team perlukan autonomy dan challenging targets'
      };
      tips.push(dopeTips[dominantDope[0]] || '');
    }
    
    return tips;
  };
  
  const communicationTips = getCommunicationTips();
  
  // Leadership insights
  const getLeadershipInsights = () => {
    const high = leadershipData['High'] || 0;
    const medium = leadershipData['Medium'] || 0;
    
    if (high > medium) {
      return { text: 'Team ada ramai potential leaders - bagus untuk succession!', color: 'text-green-600', bg: 'bg-green-50' };
    } else if (high === 0 && medium === 0) {
      return { text: 'Tiada high potential - pertimbangkan leadership development', color: 'text-red-600', bg: 'bg-red-50' };
    }
    return { text: 'Ada potential leaders - fokus develop untuk future roles', color: 'text-amber-600', bg: 'bg-amber-50' };
  };
  
  const leadershipInsight = getLeadershipInsights();
  
  return (
    <div className="space-y-6">
      {/* Section Header */}
      <div className="bg-gradient-to-r from-indigo-600 to-purple-600 rounded-2xl p-6 text-white">
        <div className="flex items-center gap-3 mb-2">
          <PieChart className="w-8 h-8" />
          <h2 className="text-2xl font-bold">Team Composition Analytics</h2>
        </div>
        <p className="text-indigo-200">Insight personaliti team untuk bantu HR & Leader buat keputusan</p>
      </div>
      
      {/* Team Health Score */}
      <TeamHealthScoreCard healthScore={healthScore} improvementTips={improvementTips} />
      
      {/* Quick Stats Row */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <div className="bg-white rounded-xl p-4 border-2 border-cyan-200 text-center">
          <p className="text-3xl font-bold text-cyan-600">{totalVakd}</p>
          <p className="text-xs text-gray-500">VAKD Dinilai</p>
        </div>
        <div className="bg-white rounded-xl p-4 border-2 border-amber-200 text-center">
          <p className="text-3xl font-bold text-amber-600">{totalDope}</p>
          <p className="text-xs text-gray-500">DOPE Dinilai</p>
        </div>
        <div className="bg-white rounded-xl p-4 border-2 border-purple-200 text-center">
          <p className="text-3xl font-bold text-purple-600">{totalColourBrain}</p>
          <p className="text-xs text-gray-500">Colour Brain</p>
        </div>
        <div className="bg-white rounded-xl p-4 border-2 border-green-200 text-center">
          <p className="text-3xl font-bold text-green-600">{leadershipData['High'] || 0}</p>
          <p className="text-xs text-gray-500">High Leaders</p>
        </div>
        <div className="bg-white rounded-xl p-4 border-2 border-blue-200 text-center">
          <p className="text-3xl font-bold text-blue-600">{Object.keys(deptData).length}</p>
          <p className="text-xs text-gray-500">Jabatan</p>
        </div>
      </div>
      
      {/* VAKD & DOPE Charts */}
      <div className="grid md:grid-cols-2 gap-6">
        <div className="bg-white rounded-2xl p-6 border border-gray-200 shadow-sm">
          <div className="flex items-center gap-2 mb-4">
            <span className="text-2xl">🧠</span>
            <h3 className="font-bold text-gray-800">VAKD - Cara Terima Maklumat</h3>
          </div>
          <div className="flex items-center gap-6">
            <MiniPieChart data={vakdData} colors={['#06b6d4', '#8b5cf6', '#f59e0b', '#10b981']} />
            <div className="flex-1 space-y-2">
              <ProgressBar label="Visual" value={vakdData['Visual'] || 0} total={totalVakd} color="bg-cyan-500" icon="👁️" />
              <ProgressBar label="Audio" value={vakdData['Audio'] || 0} total={totalVakd} color="bg-purple-500" icon="👂" />
              <ProgressBar label="Kinestetik" value={vakdData['Kinestetik'] || 0} total={totalVakd} color="bg-amber-500" icon="✋" />
              <ProgressBar label="Digital" value={vakdData['Digital'] || 0} total={totalVakd} color="bg-green-500" icon="🧠" />
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-2xl p-6 border border-gray-200 shadow-sm">
          <div className="flex items-center gap-2 mb-4">
            <span className="text-2xl">🎯</span>
            <h3 className="font-bold text-gray-800">DOPE - Cara Buat Keputusan</h3>
          </div>
          <div className="flex items-center gap-6">
            <MiniPieChart data={dopeData} colors={['#64748b', '#6366f1', '#ec4899', '#ef4444']} />
            <div className="flex-1 space-y-2">
              <ProgressBar label="Dove" value={dopeData['Dove'] || 0} total={totalDope} color="bg-slate-500" icon="🕊️" />
              <ProgressBar label="Owl" value={dopeData['Owl'] || 0} total={totalDope} color="bg-indigo-500" icon="🦉" />
              <ProgressBar label="Peacock" value={dopeData['Peacock'] || 0} total={totalDope} color="bg-pink-500" icon="🦚" />
              <ProgressBar label="Eagle" value={dopeData['Eagle'] || 0} total={totalDope} color="bg-red-500" icon="🦅" />
            </div>
          </div>
        </div>
      </div>
      
      {/* Colour Brain Distribution */}
      <div className="bg-white rounded-2xl p-6 border border-gray-200 shadow-sm">
        <div className="flex items-center gap-2 mb-4">
          <span className="text-2xl">🎨</span>
          <h3 className="font-bold text-gray-800">Colour Brain - Cara Berfikir</h3>
        </div>
        <div className="grid grid-cols-4 gap-4">
          {COLOUR_BRAIN_TYPES.map(type => {
            const count = colourBrainData[type.value] || 0;
            const percent = totalColourBrain > 0 ? ((count / totalColourBrain) * 100).toFixed(0) : 0;
            return (
              <div key={type.value} className={`rounded-xl p-4 text-center bg-gradient-to-br ${type.bgColor} text-white`}>
                <p className="text-3xl mb-1">{type.value === 'Red' ? '🔴' : type.value === 'Blue' ? '🔵' : type.value === 'Green' ? '🟢' : '🟡'}</p>
                <p className="text-2xl font-bold">{count}</p>
                <p className="text-sm opacity-90">{type.value}</p>
                <p className="text-xs opacity-75">{percent}%</p>
              </div>
            );
          })}
        </div>
      </div>
      
      {/* Leadership & Team Roles */}
      <div className="grid md:grid-cols-2 gap-6">
        <div className="bg-white rounded-2xl p-6 border border-gray-200 shadow-sm">
          <div className="flex items-center gap-2 mb-4">
            <Target className="w-6 h-6 text-purple-600" />
            <h3 className="font-bold text-gray-800">Potensi Kepimpinan</h3>
          </div>
          <div className="space-y-3 mb-4">
            <div className="flex items-center gap-3">
              <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center">
                <span className="text-2xl font-bold text-green-600">{leadershipData['High'] || 0}</span>
              </div>
              <div>
                <p className="font-medium text-gray-800">High Potential</p>
                <p className="text-xs text-gray-500">Ready for leadership</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                <span className="text-xl font-bold text-blue-600">{leadershipData['Medium'] || 0}</span>
              </div>
              <div>
                <p className="font-medium text-gray-800">Medium Potential</p>
                <p className="text-xs text-gray-500">Need development</p>
              </div>
            </div>
          </div>
          <div className={`p-3 rounded-lg ${leadershipInsight.bg}`}>
            <p className={`text-sm ${leadershipInsight.color}`}>
              <Lightbulb className="w-4 h-4 inline mr-1" />
              {leadershipInsight.text}
            </p>
          </div>
        </div>
        
        <div className="bg-white rounded-2xl p-6 border border-gray-200 shadow-sm">
          <div className="flex items-center gap-2 mb-4">
            <Users className="w-6 h-6 text-blue-600" />
            <h3 className="font-bold text-gray-800">Peranan Dalam Team</h3>
          </div>
          {Object.keys(teamRoles).length > 0 ? (
            <div className="space-y-2">
              {Object.entries(teamRoles).sort((a, b) => b[1] - a[1]).map(([role, count]) => (
                <div key={role} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <span className="font-medium">{role}</span>
                  <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-bold">{count}</span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-400 text-sm text-center py-4">Belum ada data</p>
          )}
        </div>
      </div>
      
      {/* Communication Tips */}
      <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-2xl p-6 border border-amber-200">
        <div className="flex items-center gap-2 mb-4">
          <MessageSquare className="w-6 h-6 text-amber-600" />
          <h3 className="font-bold text-amber-800">Tips Komunikasi untuk Leader</h3>
        </div>
        {communicationTips.length > 0 ? (
          <div className="space-y-3">
            {communicationTips.map((tip, i) => (
              <div key={i} className="flex items-start gap-3 p-3 bg-white rounded-lg">
                <Zap className="w-5 h-5 text-amber-500 mt-0.5" />
                <p className="text-gray-700">{tip}</p>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-amber-600 text-sm">Isi data untuk dapatkan tips</p>
        )}
      </div>
      
      {/* Department Breakdown */}
      {Object.keys(deptData).length > 0 && (
        <div className="bg-white rounded-2xl p-6 border border-gray-200 shadow-sm">
          <div className="flex items-center gap-2 mb-4">
            <Building2 className="w-6 h-6 text-indigo-600" />
            <h3 className="font-bold text-gray-800">Staff Mengikut Jabatan</h3>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {Object.entries(deptData).sort((a, b) => b[1] - a[1]).map(([dept, count]) => (
              <div key={dept} className="p-4 bg-gradient-to-br from-indigo-50 to-purple-50 rounded-xl text-center">
                <p className="text-2xl font-bold text-indigo-600">{count}</p>
                <p className="text-sm text-gray-600 truncate" title={dept}>{dept}</p>
              </div>
            ))}
          </div>
        </div>
      )}
      
      {/* Action Required */}
      {summary.total_without_details > 0 && (
        <div className="bg-gradient-to-r from-red-50 to-pink-50 rounded-2xl p-6 border border-red-200">
          <div className="flex items-center gap-2 mb-3">
            <AlertCircle className="w-6 h-6 text-red-600" />
            <h3 className="font-bold text-red-800">Tindakan Diperlukan</h3>
          </div>
          <p className="text-red-700 mb-3">
            <strong>{summary.total_without_details}</strong> staff belum lengkap maklumat
          </p>
          <div className="flex flex-wrap gap-2">
            {(summary.staff_without_details || []).slice(0, 5).map(staff => (
              <span key={staff.id} className="px-3 py-1 bg-white rounded-full text-sm text-red-600 border border-red-200">
                {staff.name}
              </span>
            ))}
            {summary.total_without_details > 5 && (
              <span className="px-3 py-1 bg-red-100 rounded-full text-sm text-red-600">
                +{summary.total_without_details - 5} lagi
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default TeamCompositionAnalytics;
